import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchReports, selectReports } from "../../../slice/reports/Reportslice";

const fmtUSD = (n) => "$" + Math.round(n || 0).toLocaleString("en-IN");
const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString("en-SG", { day: "2-digit", month: "short", year: "numeric" }) : "—";

const Reports = () => {
  const dispatch = useDispatch();
  const { overallRevenue, orders, loading } = useSelector(selectReports);

  const [search, setSearch] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  useEffect(() => {
    dispatch(fetchReports());
  }, [dispatch]);

  const dateBounds = useMemo(() => {
    const fromDate = dateFrom ? new Date(dateFrom + "T00:00:00") : null;
    const toDate = dateTo ? new Date(dateTo + "T23:59:59.999") : null;
    return { fromDate, toDate };
  }, [dateFrom, dateTo]);

  const inRange = (dateVal, fromDate, toDate) => {
    if (!fromDate && !toDate) return true;
    const d = dateVal ? new Date(dateVal) : null;
    if (!d || isNaN(d.getTime())) return false;
    if (fromDate && d < fromDate) return false;
    if (toDate && d > toDate) return false;
    return true;
  };

  // Table rows: search + date filter
  const filteredOrders = useMemo(() => {
    const q = search.trim().toLowerCase();
    const { fromDate, toDate } = dateBounds;
    return orders.filter((o) => {
      if (q && !(o.orderId || "").toLowerCase().includes(q)) return false;
      return inRange(o.date, fromDate, toDate);
    });
  }, [orders, search, dateBounds]);

  // Overall revenue: reacts to BOTH search + date, so the card always
  // matches exactly what's shown in the table below
  const computedOverallRevenue = useMemo(() => {
    const hasFilters = Boolean(search.trim() || dateFrom || dateTo);
    if (!hasFilters) return overallRevenue;
    return filteredOrders.reduce((sum, o) => sum + (o.total || 0), 0);
  }, [search, dateFrom, dateTo, overallRevenue, filteredOrders]);

  const isDateFiltered = Boolean(dateFrom || dateTo);

  const clearAllFilters = () => {
    setSearch("");
    setDateFrom("");
    setDateTo("");
  };

  const hasActiveFilters = search.trim() !== "" || dateFrom || dateTo;

  return (
    <div>
      <div style={{ paddingTop: 10, marginBottom: 20 }}>
        <h1 style={{ fontSize: 22, fontWeight: 800, color: "#111827", margin: 0 }}>Reports</h1>
        <div style={{ fontSize: 13, color: "#9ca3af", marginTop: 4 }}>
          Revenue breakdown for delivered orders
        </div>
      </div>

      {isDateFiltered && (
        <div style={{
          display: "inline-flex", alignItems: "center", gap: 6,
          padding: "6px 12px", borderRadius: 20, background: "#eef2ff",
          color: "#4338ca", fontSize: 12, fontWeight: 700, marginBottom: 12,
        }}>
          📅 Revenue shown for {dateFrom ? fmtDate(dateFrom) : "Any"} → {dateTo ? fmtDate(dateTo) : "Any"}
        </div>
      )}

      {/* ── Overall revenue + filters, same row ── */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 16,
          background: "#fff",
          borderRadius: 16,
          padding: "18px 20px",
          boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
          marginBottom: 24,
        }}
      >
        <div>
          <div style={{ fontSize: 12, fontWeight: 600, color: "#9ca3af", marginBottom: 6 }}>
            Overall Revenue
          </div>
          <div style={{ fontSize: 26, fontWeight: 800, color: "#111827" }}>
            {fmtUSD(computedOverallRevenue)}
          </div>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          {/* Search */}
          <div style={{ position: "relative" }}>
            <svg
              width="14" height="14" viewBox="0 0 24 24" fill="none"
              stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
              style={{ position: "absolute", left: 11, top: "50%", transform: "translateY(-50%)" }}
            >
              <circle cx="11" cy="11" r="8" />
              <path d="m21 21-4.3-4.3" />
            </svg>
            <input
              type="text"
              placeholder="Search order ID..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{
                width: 200,
                padding: "8px 12px 8px 32px",
                borderRadius: 9,
                border: "1px solid #e5e7eb",
                fontSize: 13,
                outline: "none",
                background: "#f9fafb",
                boxSizing: "border-box",
              }}
            />
          </div>

          {/* Date range */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              background: "#f9fafb",
              border: "1px solid #e5e7eb",
              borderRadius: 9,
              padding: "5px 10px",
            }}
          >
            <input
              type="date"
              value={dateFrom}
              max={dateTo || undefined}
              onChange={(e) => setDateFrom(e.target.value)}
              style={{
                border: "none", background: "transparent", fontSize: 13,
                color: dateFrom ? "#111827" : "#9ca3af", outline: "none",
                padding: "3px 2px", fontWeight: 600,
              }}
            />
            <span style={{ color: "#d1d5db", fontSize: 12 }}>→</span>
            <input
              type="date"
              value={dateTo}
              min={dateFrom || undefined}
              onChange={(e) => setDateTo(e.target.value)}
              style={{
                border: "none", background: "transparent", fontSize: 13,
                color: dateTo ? "#111827" : "#9ca3af", outline: "none",
                padding: "3px 2px", fontWeight: 600,
              }}
            />
          </div>

          {hasActiveFilters && (
            <button
              type="button"
              onClick={clearAllFilters}
              style={{
                padding: "8px 14px",
                borderRadius: 9,
                border: "1px solid #fecaca",
                background: "#fef2f2",
                color: "#dc2626",
                fontSize: 12,
                fontWeight: 700,
                cursor: "pointer",
                whiteSpace: "nowrap",
              }}
            >
              Clear
            </button>
          )}
        </div>
      </div>

      {/* ── Table ── */}
      <div style={{ background: "#fff", borderRadius: 16, overflow: "hidden", boxShadow: "0 1px 3px rgba(0,0,0,0.05)" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ background: "#fafbfc", borderBottom: "1px solid #eef0f3" }}>
              {["ORDER ID", "DATE", "MINIMUM CART FEE", "SERVICE CHARGE", "DELIVERY CHARGE", "TOTAL"].map((h) => (
                <th key={h} style={{
                  textAlign: "left", padding: "14px 20px", fontSize: 11,
                  fontWeight: 700, color: "#9ca3af", letterSpacing: 0.5,
                }}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={6} style={{ padding: "40px 20px", textAlign: "center", color: "#9ca3af" }}>
                  Loading...
                </td>
              </tr>
            ) : filteredOrders.length === 0 ? (
              <tr>
                <td colSpan={6} style={{ padding: "40px 20px", textAlign: "center", color: "#9ca3af" }}>
                  {orders.length === 0 ? "No delivered orders yet." : "No orders match your filters."}
                </td>
              </tr>
            ) : (
              filteredOrders.map((o, idx) => (
                <tr key={o.orderId} style={{ borderBottom: idx < filteredOrders.length - 1 ? "1px solid #f3f4f6" : "none" }}>
                  <td style={{ padding: "16px 20px", fontSize: 14, fontWeight: 700, color: "#1f2937" }}>
                    {o.orderId}
                  </td>
                  <td style={{ padding: "16px 20px", fontSize: 14, color: "#374151" }}>
                    {fmtDate(o.date)}
                  </td>
                  <td style={{ padding: "16px 20px", fontSize: 14, color: "#374151" }}>
                    {fmtUSD(o.minimumOrderFee)}
                  </td>
                  <td style={{ padding: "16px 20px", fontSize: 14, color: "#374151" }}>
                    {fmtUSD(o.serviceCharge)}
                  </td>
                  <td style={{ padding: "16px 20px", fontSize: 14, color: "#374151" }}>
                    {fmtUSD(o.shipping)}
                  </td>
                  <td style={{ padding: "16px 20px", fontSize: 14, fontWeight: 700, color: "#059669" }}>
                    {fmtUSD(o.total)}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Reports;